import { useState } from 'react';
import { CaretIcon, CheckmarkIcon } from '../../../assets/icons';

interface EcNode {
  id: string;
  label: string;
  selectable?: boolean; // renders a checkbox and can be the selected boundary
  children?: EcNode[];
}

/** EC level hierarchy sample data (shared by steps 2 & 3). */
const EC_TREE: EcNode[] = [
  {
    id: 'assembly',
    label: 'Assembly Constituency',
    children: [
      {
        id: 'urban',
        label: 'Urban',
        selectable: true,
        children: [
          {
            id: 'municipality',
            label: 'Municipality',
            children: [
              { id: 'ward', label: 'Ward', children: [{ id: 'urban-booth', label: 'Booth' }] },
            ],
          },
        ],
      },
      {
        id: 'rural',
        label: 'Rural',
        selectable: true,
        children: [
          {
            id: 'panchayat',
            label: 'Panchayat Samiti',
            children: [
              {
                id: 'gram',
                label: 'Gram Panchayat',
                children: [
                  {
                    id: 'village',
                    label: 'Village',
                    children: [{ id: 'rural-booth', label: 'Booth' }],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

function useToggleSet(initial: string[]) {
  const [set, setSet] = useState<Set<string>>(new Set(initial));
  const has = (id: string) => set.has(id);
  const toggle = (id: string) =>
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return { has, toggle };
}

interface EcHierarchyTreeProps {
  /** Override the root node label (e.g. "Alwar Assembly Constituency"). */
  rootLabel?: string;
}

/** Single-select, fully expandable Election Commission level hierarchy. */
export default function EcHierarchyTree({ rootLabel }: EcHierarchyTreeProps) {
  const expand = useToggleSet(['assembly', 'rural', 'panchayat', 'gram', 'village']);
  const [selected, setSelected] = useState<string>('rural');

  const renderNode = (node: EcNode, depth: number) => {
    const hasChildren = Boolean(node.children?.length);
    const open = expand.has(node.id);
    const isSelected = node.selectable === true && selected === node.id;
    const label = depth === 0 && rootLabel ? rootLabel : node.label;
    return (
      <div key={node.id}>
        <div
          className={
            isSelected
              ? 'flex items-center gap-2 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-2 py-2.5'
              : 'flex items-center gap-2 py-1.5'
          }
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => expand.toggle(node.id)}
              className="flex size-4 shrink-0 items-center justify-center text-[#94a3b8]"
              aria-label={open ? 'Collapse' : 'Expand'}
            >
              <CaretIcon className={['size-4 transition-transform', open ? '' : '-rotate-90'].join(' ')} />
            </button>
          ) : (
            <span className="size-4 shrink-0" />
          )}

          {node.selectable && (
            <button
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              onClick={() => setSelected(isSelected ? '' : node.id)}
              className={[
                'flex size-[18px] shrink-0 items-center justify-center rounded transition-colors',
                isSelected ? 'bg-brand text-white' : 'border border-[#cbd5e1] bg-white',
              ].join(' ')}
            >
              {isSelected && <CheckmarkIcon className="size-4" />}
            </button>
          )}

          <button
            type="button"
            onClick={() =>
              node.selectable
                ? setSelected(isSelected ? '' : node.id)
                : hasChildren && expand.toggle(node.id)
            }
            className={[
              'text-left text-sm',
              isSelected
                ? 'font-semibold text-brand'
                : depth === 0
                  ? 'font-semibold text-ink'
                  : 'font-normal text-ink-secondary',
            ].join(' ')}
          >
            {label}
          </button>

          {isSelected && (
            <span className="ml-auto text-[10px] font-bold tracking-[0.5px] text-brand">SELECTED</span>
          )}
        </div>

        {hasChildren && open && (
          <div className="ml-2 flex flex-col gap-1 border-l border-line-soft pl-4">
            {node.children!.map((c) => renderNode(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return <div className="flex flex-col gap-1 text-sm">{EC_TREE.map((n) => renderNode(n, 0))}</div>;
}
